import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Shield, FileText, ArrowLeft, Clock } from 'lucide-react';

const LEGAL_CONTENT = {
    'privacy-policy': {
        title: 'Privacy Policy',
        tag: 'DATA PROTECTION',
        icon: <Shield className="w-12 h-12 text-nocturne-purple" />,
        lastUpdated: 'May 20, 2024',
        sections: [
            {
                title: 'Data Collection',
                content: 'We collect information that you provide directly to us when you fill out forms, subscribe to our newsletter, or communicate with us. This may include your name, email address, and any other information you choose to provide.'
            },
            {
                title: 'How We Use Data',
                content: 'We use the information we collect to provide, maintain, and improve our services, to communicate with you about projects, and to send you technical notices, updates, and security alerts.'
            },
            {
                title: 'Information Sharing',
                content: 'We do not share your personal information with third parties except as described in this policy, such as with your consent or for legal reasons.'
            },
            {
                title: 'Data Security',
                content: 'We take reasonable measures to help protect information about you from loss, theft, misuse and unauthorized access, disclosure, alteration and destruction.'
            }
        ]
    },
    'terms-of-service': {
        title: 'Terms of Service',
        tag: 'LEGAL FRAMEWORK',
        icon: <FileText className="w-12 h-12 text-nocturne-purple" />,
        lastUpdated: 'May 20, 2024',
        sections: [
            {
                title: 'Acceptance of Terms',
                content: 'By accessing or using our services, you agree to be bound by these Terms of Service. If you do not agree to all of these terms, do not use our services.'
            },
            {
                title: 'Services Description',
                content: 'Our agency provides digital services including but not limited to web development, software architecture, and digital strategy. Specific deliverables and timelines are governed by individual project agreements.'
            },
            {
                title: 'Intellectual Property',
                content: 'Unless otherwise agreed in a written contract, all materials, including software, designs, and content created by our agency remain our intellectual property until full payment is received.'
            },
            {
                title: 'Limitation of Liability',
                content: 'To the maximum extent permitted by law, our agency shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services.'
            }
        ]
    }
};

export default function LegalPage() {
    const location = useLocation();
    const type = location.pathname.split('/').pop() || 'privacy-policy';
    const content = LEGAL_CONTENT[type] || LEGAL_CONTENT['privacy-policy'];

    return (
        <div className="min-h-screen pt-16">
            {/* Header Hero */}
            <section className="py-16 px-6 pb-6 sm:px-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-hero-glow pointer-events-none opacity-50" />
                <div className="max-w-4xl mx-auto relative z-10">
                    <Link
                        to="/"
                        className="text-nocturne-muted hover:text-nocturne-text transition-colors text-sm mb-12 flex items-center gap-2"
                    >
                        <ArrowLeft size={14} /> Back to Home
                    </Link>

                    <div className="flex flex-col md:flex-row gap-8 items-start mb-12">
                        <div className="w-20 h-20 rounded-2xl bg-nocturne-purple/10 border border-nocturne-purple/30 flex items-center justify-center shrink-0">
                            {content.icon}
                        </div>
                        <div>
                            <p className="section-tag mb-2">{content.tag}</p>
                            <h1 className="text-5xl md:text-6xl font-black text-nocturne-text leading-tight mb-4 uppercase">
                                {content.title}
                            </h1>
                        </div>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-6 px-6 sm:px-8 pb-32">
                <div className="max-w-4xl mx-auto">
                    <div className="card border-nocturne-purple/20 bg-nocturne-card/30 backdrop-blur-md p-8 md:p-12 space-y-12">
                        {content.sections.map((section, idx) => (
                            <div key={idx} className="group">
                                <h2 className="text-2xl font-bold text-nocturne-text mb-4 flex items-center gap-3">
                                    <span className="text-nocturne-purple/40 font-mono text-sm">0{idx + 1}</span>
                                    {section.title}
                                </h2>
                                <p className="text-nocturne-muted leading-relaxed text-lg pl-8 border-l border-nocturne-border group-hover:border-nocturne-purple transition-colors">
                                    {section.content}
                                </p>
                            </div>
                        ))}
                        
                     
                    </div>
                </div>
            </section>
        </div>
    );
}
